// @flow

import _ from 'lodash';
import React from 'react';
import moment from 'moment-timezone';
import { TextStyle } from '@shopify/polaris';
import xss from 'xss';

import { app } from 'src/config/';
import { store } from 'src/stores/store';
import { slugify as slugifyTransliteration } from 'transliteration';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { DATE_FORMAT } from 'src/constants/language-setting';
import {
  ACCELERATE,
  BASIC,
  CONVERT,
  ESSENTIAL,
  FREEMIUM,
  LAUNCH,
  PROFESSIONAL,
  ENTERPRISE
} from 'src/constants/subscription';

/**
 * Get the tenant Id
 *
 * @result {string} value of tenant ID
 */
export function getShop() {
  const currentState = store.getState();

  return _.get(currentState, 'oauth.tenantApp.tenantId', '');
}

/**
 * Get the full url front store
 *
 * @param  {string}    routePath route name
 *
 * @result {string} full url of route
 */
export function getFrontStoreLink() {
  return `https://${getShop()}`;
}

/**
 * Get the tenant name
 *
 * @result {string} value of tenant ID
 */
export function getTenantName() {
  const tenantId = getShop();
  const tenantName = tenantId.match(/(.*).myshopify.com/);
  if (tenantName && tenantName[1]) {
    return tenantName[1];
  }
  return '';
}

export const isBoostDevStore = (tenantId: string): boolean => {
  // 'bc-dev', 'boost-pfs', 'bc-sf-filter-demo', 'boost-sd'
  return _.some(['bc-dev', 'bc-sf-filter-demo', 'boost-sd'], prefix => {
    return _.startsWith(tenantId, prefix);
  });
};

/**
 * Build redirect url by following new shopify policy
 * @param {*} tenantId
 * @param {*} routePath
 * @returns
 */
export function buildRedirectUrl(tenantId: string, routePath: string = ''): string {
  const redirectUrl = `https://admin.shopify.com/store/${_.replace(
    tenantId,
    '.myshopify.com',
    ''
  )}/apps/${app.apiKey}`;

  const link = routePath ? `${redirectUrl}${routePath}` : redirectUrl;

  console.log(`Build link with route path ${routePath} => ${link}`);

  return link;
}

/**
 * Get the full url for route
 *
 * @param  {string}    routePath route name
 *
 * @result {string} full url of route
 */
export function getFullAppUrl(routePath: string) {
  return `https://${getShop()}/admin/apps/${app.apiKey}${routePath}${window.location.search}`;
}

/**
 * Get the full url for theme editor
 *
 * @param  {string}    routePath route name
 *
 * @result {string} full url of route
 */
export function getFullAppUrlThemeEditor(themeId: string, query = '') {
  if (!themeId) return `https://admin.shopify.com/store/${getTenantName()}/themes`;
  return `https://admin.shopify.com/store/${getTenantName()}/themes/${themeId}/editor${query}`;
}

/**
 * Get link access to Theme code editor
 * @param {*} themeId
 * @returns
 */
export function getFullAppUrlThemeCodeEditor(themeId: string) {
  if (!themeId) return `https://admin.shopify.com/store/${getTenantName()}/themes`;
  return `https://admin.shopify.com/store/${getTenantName()}/themes/${themeId}`;
}

/**
 * Get the full url for collection
 *
 * @param  {string}    routePath route name
 *
 * @result {string} full url of route
 */
export function getCollectionsUrl() {
  return `https://admin.shopify.com/store/${getTenantName()}/collections`;
}

/**
 * Get the full url for themes
 *
 * @param  {string}    routePath route name
 *
 * @result {string} full url of route
 */
export function getFullAppUrlThemes() {
  return `https://admin.shopify.com/store/${getTenantName()}/themes`;
}

/**
 * Get the full url for route
 *
 * @param  {string}    routePath route name
 *
 * @result {string} full url of route
 */
export function getFullAppUrlV2(routePath: string) {
  const queryParams = window.location.search || '';
  const path = routePath.includes('?')
    ? `${routePath}${queryParams.replace('?', '&')}`
    : `${routePath}${queryParams}`;

  return `https://${getShop()}/admin/apps/${app.apiKey}${path}`;
}

/**
 * Get the URL for theme review
 *
 * @param  {string} themeId
 *
 * @result {string} full url of theme review
 */
export function getThemeReviewUrl(themeId: string) {
  return `https://${getShop()}/collections/all?preview_theme_id=${themeId}`;
}

/**
 * Get the URL for theme review
 *
 * @param  {string} themeId
 *
 * @result {string} full url of theme review
 */
export function getThemePreviewUrl(themeId: string, query: string, collection: string = null) {
  if (query) {
    const q = _.lowerCase(query);
    return `https://${getShop()}/search?preview_theme_id=${themeId}&q=${q}`;
  }
  if (collection)
    return `https://${getShop()}/collections/${collection}?preview_theme_id=${themeId}`;
  return `https://${getShop()}/collections/all?preview_theme_id=${themeId}`;
}

/**
 * Get the URL for calling auth from shopify
 *
 * @param  {string} tenantId
 *
 * @result {string} full url of auth
 */
export function buildAuthURL(tenantId: string) {
  const authUrl = `https://${tenantId}/admin/oauth/authorize?client_id=${app.apiKey}&scope=${app.appScope}&redirect_uri=${app.installCallbackUrl}&state=${app.nonce}`;

  return authUrl;
}

/**
 * Get the URL for updating app permission
 *
 * @param  {string} tenantId
 *
 * @result {string} full url of auth
 */
export function buildUpdateAppPermissionURL(tenantId: string, scope: string) {
  const authUrl = `https://${tenantId}/admin/oauth/authorize?client_id=${app.apiKey}&scope=${scope || app.appScope}&redirect_uri=${app.updatePermissionCallbacklUrl}&state=${app.nonce}`;

  return authUrl;
}

/**
 * Get the URL for updating app permission
 *
 * @param  {string} tenantId
 *
 * @result {string} full url of auth
 */
export function buildUpdateAppPermissionURLV2(routePath: string) {
  const queryParams = window.location.search || '';
  const path = routePath.includes('?')
    ? `${routePath}${queryParams.replace('?', '&')}`
    : `${routePath}${queryParams}`;

  const authUrl = `https://${getShop()}/admin/oauth${path}`;

  return authUrl;
}

/**
 * Get now time stamp in second
 *
 * @return {number} timestamp
 */
export function nowTimestamp() {
  return Math.floor(Date.now() / 1000);
}
/**
 * Get Unix Timestamp In Seconds
 *
 * @param isoDateString ISO Date String
 * @return {number} timestamp
 */
export const getUnixTimestamp = (date): number => {
  return Math.round(date.getTime() / 1000);
};

/**
 * Calculate date difference from current time
 *
 * @param  {number} datetime time in second
 *
 * @return {number}          day diff
 */
export function getDayDiff(datetime: number = 0) {
  const now = nowTimestamp();
  const timeDiff = Math.abs(now - datetime);

  let days = Math.ceil(timeDiff / 60 / (60 * 24));
  days = !days ? 1 : days;

  return datetime < now ? -1 * days : days;
}

/**
 * Convert timestamp to datetime
 *
 * @param  {number} timestamp
 *
 * @return {string} datetime
 */
export function formatDatetime(timestamp: number) {
  // Check correct time stamp
  let timestampInt =
    timestamp.toString().length > 10 ? parseInt(timestamp) : parseInt(timestamp) * 1000;
  if (!timestampInt) return;

  const timeDiff = moment().diff(moment(timestampInt), 'minutes');

  if (timeDiff < 5) {
    return moment(timestampInt).fromNow();
  }

  const currentState = store.getState();
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  // Format date with language
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDate = DATE_FORMAT[appLanguage];

  if (timezone) {
    const storeDate = moment.tz(timestampInt, timezone);
    return storeDate.format(formatDate.fullDateNumber);
  }
  return moment(timestampInt).format(formatDate.fullDateNumber);
}

/** Format text to send to server
 *
 * @param  {string} text text need to be formatted
 *
 * @return {string} formatted text
 */
export function prettyText(text: string) {
  return text ? text.trim().replace(/<[^>]+>/g, '') : '';
}

/**
 * Remove extra white space in text or string
 * @param {*} str
 * @returns
 */
export const removeExtraSpace = (str: string) => {
  if (!str) {
    return '';
  }
  return str.replace(/\s+/g, ' ').trim();
};

/** Create html markup that allows to render html in react component
 * @param  {string} html
 *
 * @return {object}      react component
 */
export function createMarkup(html: string) {
  return { __html: html };
}

/**
 * Check if the app is loaded in iframe
 *
 * @return {boolean} true | false
 */
export function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return false;
  }
}

/**
 * Check Localstorage is available
 * @returns
 */
export function isLocalStorageAvailable() {
  const test = 'test';
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);

    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Capitalize first letter of a string
 *
 * @return {string}
 */
export function capitalizeFirstLetter(string: string, locale: string) {
  if (!string) {
    return '';
  }

  return string.charAt(0).toLocaleUpperCase(locale) + string.slice(1);
}

// Convert array/object to string
export function convertArrayToString(obj: Object, separate: string = ',', type: ?string = null) {
  let str = '';
  for (let k = 0; k < obj.length; k++) {
    str += obj[k];
    if (k < obj.length - 1 || type === 'error') {
      if (separate === '|') {
        str += ' ' + separate + ' ';
      } else {
        str += separate;
      }
    }
  }
  return str;
}

// Convert text to slug
export function slugify(sourceText: string) {
  if (!sourceText) {
    return '';
  }

  // Slugify with limax
  // let text = slugLimax(sourceText);

  // Slugify with transliteration
  // if (_.isEmpty(text)) {
  //   text = slugifyTransliteration(sourceText);
  // }

  let text = slugifyTransliteration(sourceText);

  // Remove/replace non character if needed
  text = text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/-+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text

  return text;
}

/**
 * Round float percent to integer
 * @param  {number} num percent
 * @return {number}
 */
export const percentRoundFn = (num: number) => Math.round(num * 100) / 100;

/**
 * Display number in thousand formatting
 * @param  {number} value - The number to format
 * @param  {boolean} isShowSimple - If true, shows abbreviated format (M, B, T, Q)
 * @return {string} Formatted string with thousand separators or abbreviations
 *
 * @example
 * formatNumberInThousand(1234567) // "1,234,567"
 * formatNumberInThousand(1234567, true) // "1.23M"
 * formatNumberInThousand(1234567890, true) // "1.23B"
 */
export const formatNumberInThousand = (
  value: number,
  isShowSimple: boolean,
  removeDecimal: boolean
) => {
  if (!value && value !== 0) return;

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (isShowSimple) {
    // Quadrillion
    if (absValue >= 1e15) {
      return `${sign}${(absValue / 1e15).toFixed(2)}Q`;
    }
    // Trillion
    if (absValue >= 1e12) {
      return `${sign}${(absValue / 1e12).toFixed(2)}T`;
    }
    // Billion
    if (absValue >= 1e9) {
      return `${sign}${(absValue / 1e9).toFixed(2)}B`;
    }
    // Million
    if (absValue >= 1e6) {
      return `${sign}${(absValue / 1e6).toFixed(2)}M`;
    }

    // Thousand
    if (absValue >= 1e4) {
      return `${sign}${(absValue / 1e3).toFixed(2)}K`;
    }
  }

  // Format with thousand separators
  const [integer, decimal] = absValue.toString().split('.');
  const formatInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (decimal && decimal !== '' && !removeDecimal) {
    return `${sign}${formatInteger}.${decimal}`;
  }
  return `${sign}${formatInteger}`;
};

/**
 * It takes a number and a locale and returns a string
 * @param {number} value - number
 * @param [string=en-US] - The string to be formatted.
 * @returns A function that takes two arguments, value and locale, and returns a string.
 */
export const formatNumberByLocale = (value: number, locale: string = 'en-US') => {
  return value.toLocaleString(locale, { maximumFractionDigits: 2 });
};

/**
 * Display number in money formatting
 * @param  {number} value
 * @return {string}       string under formatting
 */
export const formatNumberWithMoney = (value: number) => {
  const currentState = store.getState();
  const moneyFormat = _.get(currentState, 'oauth.tenantApp.externalInfo.money_format', '');

  if (!value && value !== 0) return;

  // https://help.shopify.com/en/manual/payments/currency-formatting#currency-formatting-options
  if (!_.isEmpty(moneyFormat)) {
    return moneyFormat
      .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/, value)
      .replace(/{{\s*amount_no_decimals\s*}}/, value)
      .replace(/{{\s*amount_with_comma_separator\s*}}/, value)
      .replace(/{{\s*amount_with_space_separator\s*}}/, value)
      .replace(/{{\s*amount_with_apostrophe_separator\s*}}/, value)
      .replace(/{{\s*amount_without_comma_separator\s*}}/, value)
      .replace(/{{\s*amount\s*}}/, value);
  }

  return value;
};

/**
 * Remove empty attribute in object
 * @param  {object} object
 * @return {object}
 */
export const removeEmpty = (object: Object) => {
  return _.pickBy(object, field => {
    return !_.isEmpty(field);
  });
};

/**
 * Export to file
 *
 * @param  {object} blob     content
 * @param  {string} fileName file name
 */
export const downloadFile = (blob: Object, fileName: string) => {
  // Create blob link to download
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);

  // Append to html page
  // $FlowFixMe
  document.body.appendChild(link);

  // Force download
  link.click();
  // Clean up and remove the link
  // $FlowFixMe
  link.parentNode.removeChild(link);
};

/**
 * Flatten object
 *
 * @param  {object} inputObject
 * @return {object}             flat object
 */
export const flattenObject = (inputObject: Object) => {
  let outputObject = {};

  Object.keys(inputObject).forEach(key => {
    if (_.isObject(inputObject[key])) {
      const flatObject = flattenObject(inputObject[key]);
      Object.keys(flatObject).forEach(flatKey => {
        outputObject[flatKey] = flatObject[flatKey];
      });
    } else {
      outputObject[key] = inputObject[key];
    }
  });

  return outputObject;
};

/**
 * Flatten array object to single object
 *
 * @param  {array} inputArray
 *                 [
 *                   { key: <string>, value: <any>}
 *                 ]
 *
 * @return {object} output object
 *                 {
 *                   key: value <any>
 *                 }
 */
export const flattenArrayObject = (inputArray: Array<{ key: string, value: string }>) => {
  let outputObject = {};

  inputArray.forEach(item => {
    const { key, value } = item;
    outputObject[key] = value;
  });

  return outputObject;
};

/**
 * remove html tags and special characters
 *
 * @param  {string} query
 *
 * @return {string}
 */
export const removeScriptTag = (query: string) => {
  // Remove HTML Special characters
  query = query.replace(/&#?[a-z0-9]+;/g, ' ');
  // Remove HTML Tags
  query = query.replace(/<\/?[^>]+(>|$)/g, '');

  return query;
};

/**
 * Helper to searchify search query
 *
 * @param  {string}  query
 *
 * @return {string}
 */
export const searchify = (query: ?string) => {
  query = typeof query == 'string' || query instanceof String ? query.trim().toLowerCase() : null;
  if (query) {
    // Replace + symbol with space -- Not use with new requirement
    // query = query.replace(/\+/g, ' ');
    // Remove HTML Special characters
    query = query.replace(/&#?[a-z0-9]+;/g, ' ');
    // Remove HTML Tags
    query = query.replace(/<\/?[^>]+(>|$)/g, '');
    // remove allowed special characters but stand alone
    query = query
      .replace(/(\s+|^)([~`+-\-!@#$%^&*()_={}[\]|:;"'<>,.?/\\\s]+)(\s+|$)/g, ' ')
      .replace(/\s\s+/g, ' ');
    return query.trim();
  }

  return '';
};

/**
 * Check special character
 *
 * @param  {string}  query
 *
 * @return {string}
 */
export const checkHasSpecialCharacters = (
  query: ?string,
  exception: string = '',
  allowConsecutiveWhitespace = false
) => {
  let specialString = '~`+-\\-!@#$%^&*()_={}[\\]|:;"\'<>,.?/\\\\\\s';
  if (!_.isEmpty(exception)) {
    const exceptionRegex = new RegExp('[' + exception + ']', 'g');
    specialString = specialString.replace(exceptionRegex, '');
  }

  let regexString = new RegExp('(\\s+|^)([' + specialString + ']+)(\\s+|$)', 'g');

  query = typeof query == 'string' || query instanceof String ? query.trim().toLowerCase() : null;

  if (query) {
    let condition =
      query.match(/&#?[a-z0-9]+;/g) ||
      query.match(/<\/?[^>]+(>|$)/g) ||
      query.match(regexString) ||
      /<.*?>/g.test(query); // Check has tag (<any-value>)

    if (allowConsecutiveWhitespace) {
      return !!condition;
    }
    return !!(condition || query.match(/\s\s+/g));
  }

  return false;
};

/**
 * Check special character for product title - accept character is -
 *
 * @param  {string}  query
 *
 * @return {string}
 */
export const checkHasSpecialCharactersProduct = (query: ?string, exception: string = '') => {
  let specialString = '~`+\\!@#$%^&*()_={}[\\]|:;"\'<>,.?/\\\\\\s';
  if (!_.isEmpty(exception)) {
    const exceptionRegex = new RegExp('[' + exception + ']', 'g');
    specialString = specialString.replace(exceptionRegex, '');
  }

  let regexString = new RegExp('(\\s+|^)([' + specialString + ']+)(\\s+|$)', 'g');

  query = typeof query == 'string' || query instanceof String ? query.trim().toLowerCase() : null;

  if (query) {
    return query.match(/&#?[a-z0-9]+;/g)
      ? true
      : query.match(/<\/?[^>]+(>|$)/g)
        ? true
        : query.match(regexString)
          ? true
          : query.match(/\s\s+/g)
            ? true
            : false;
  }
  return false;
};
/**
 * Remove unexpected chars in terms
 *
 * @param  {string} terms terms
 *
 * @return {string}       updated terms
 */
export const removeUnexpectedCharsInTerms = (terms: string) => {
  let updatedTerms = searchify(terms);
  updatedTerms = updatedTerms ? updatedTerms.trim() : updatedTerms;

  return updatedTerms;
};

/**
 * Sort properties in object
 *
 * @param  {object} obj            input object
 * @param  {Array}  sortOrderProps list of arranged props
 *
 * @return {object}                updated object
 */
export const orderObjectProperties = (obj: Object, sortOrderProps: Array<Object> = []) => {
  // Get all props
  const allProps = Object.keys(obj).map(key => {
    return key;
  });

  // Sort props
  const newOrderProps = _.concat(sortOrderProps, _.difference(allProps, sortOrderProps));

  // Arrange props in object
  const updatedObj = {};
  newOrderProps.forEach(key => {
    updatedObj[key] = obj[key];
  });

  return updatedObj;
};

/**
 * Get first character of string
 *
 * @param  {string} str
 *
 * @return {string}     first character
 */
export const getFirstCharacter = (str: string) => {
  if (!str) return '';

  return str.charAt(0);
};

/**
 * Resize image product
 *
 * @param  {string} str
 *
 * @return {string}     first character
 */
export const optimizeImage = function (imageUrl: string, size: string) {
  // If url is null, get default image
  if (!imageUrl) return '';

  // Get the suitable size
  let sizeOptimize = typeof size !== 'undefined' ? size : 'large';
  const ext = ['jpg', 'JPG', 'png', 'PNG', 'jpeg', 'JPEG', 'gif', 'GIF'];

  for (let k = 0; k < ext.length; k++) {
    imageUrl = imageUrl.replace('.' + ext[k] + '?', '_' + sizeOptimize + '.' + ext[k] + '?');
  }
  return imageUrl;
};

const htmlTagRegexp = new RegExp(
  '</?(?:article|aside|bdi|command|' +
    'details|dialog|summary|figure|figcaption|footer|header|hgroup|mark|' +
    'meter|nav|progress|ruby|rt|rp|section|time|wbr|audio|' +
    'video|source|embed|track|canvas|datalist|keygen|output|' +
    '!--|!DOCTYPE|a|abbr|address|area|b\\s|b>|base|bdo|blockquote|body|' +
    'br|button|canvas|caption|cite|code|col|colgroup|dd|del|dfn|div|' +
    'dl|dt|em|embed|fieldset|figcaption|figure|footer|form|h1|h2|h3|h4|' +
    'h5|h6|head|hr|html|i|iframe|img|input|ins|kdb|keygen|label|legend|' +
    'li|link|map|menu|meta|noscript|object|ol|optgroup|option|p|param|' +
    'pre|q\\s|samp|script|select|small|source|span|strong|style|sub|' +
    'sup|table|tbody|td|textarea|tfoot|th|thead|title|tr|ul|var|' +
    'acronym|applet|basefont|big|center|dir|font|frame|marquee|svg|kay|' +
    'frameset|noframes|strike|tt)(?:(?: [^<>]*)>|>?)',
  'i'
);

// Define XSS validation options, such as whitelisting acceptable HTML tags
// For more detail how this package sanitize data, see here https://www.npmjs.com/package/xss
const xssOptions = {
  // stripIgnoreTag === true: filter out tags not in the whitelist
  stripIgnoreTag: false,
  // Not escape single special character, like < > &
  escapeHtml: function (html) {
    return html;
  },
  // Ignore tags which are not in white list
  onIgnoreTag: function (tag, html) {
    if (html.match(htmlTagRegexp)) return '';
    return html;
  },

  onIgnoreTagAttr: function (tag, name, value) {
    if (name.substr(0, 2) !== 'on') {
      // escape its value using built-in escapeAttrValue function
      return name + '="' + xss.escapeAttrValue(value) + '"';
    }
  }
};

// Generate XSS validation instance
export const myXss = new xss.FilterXSS(xssOptions);

/**
 * Check has xss issue
 *
 * @param  {string}  query
 *
 * @return {string}
 */
export const checkHasXss = (query: ?string) => {
  const sanitizedValue = myXss.process(query);
  return sanitizedValue !== query;
};

/**
 * Sanitize input to prevent XSS attack
 *
 * @param  {any} input
 *
 * @return {any} Return an sanitized object
 */
export const xssSanitizeInput = (input: Object) => {
  const _input = _.cloneDeep(input);

  // If input is an array, sanitize all elements inside it
  if (_.isArray(_input)) {
    return _input.map(ele => xssSanitizeInput(ele));
  }
  // If input is an object, sanitize value of each fields
  else if (typeof _input === 'object' && _input !== null) {
    const output = {};
    for (const [key, value] of Object.entries(_input)) {
      output[key] = xssSanitizeInput(value);
    }
    return output;
  }
  // If input is an string, sanitize its content
  else if (typeof _input === 'string') {
    // If the string contains any HTML tags that are excluded from the white list, sanitize its content
    const sanitizedInput = myXss.process(_input);
    if (sanitizedInput.match(htmlTagRegexp)) return '';
    return sanitizedInput;
  }
  // Ignore if input is of other types, such as number, boolean, null, undefined,...
  return _input;
};

/**
 * Build option list from constant value
 *
 * @param  {Array} constantOptions
 *
 * @return {string}     first character
 */
export const buildOptionList = (
  constantOptions: Array<{ value: string, label: string, labelAddition: Array<Object> }>,
  i18n: Object
) => {
  if (_.isEmpty(constantOptions)) {
    return [];
  }

  const result = _.map(constantOptions, function (option) {
    let { tooltip = undefined, label, labelAddition = undefined } = option;
    // tooltip
    if (_.isString(tooltip) && tooltip.includes('AdminHTML')) {
      tooltip = i18n.translate(tooltip);
    }
    // Label
    if (!_.isEmpty(labelAddition)) {
      let labelAdditionValues = {};
      _.map(labelAddition, item => {
        switch (item.type) {
          case 'strong': {
            labelAdditionValues[item.name] = (
              <TextStyle variation="strong">{i18n.translate(item.label)}</TextStyle>
            );
            break;
          }
          default:
        }
        return {};
      });
      label = i18n.translate(label, labelAdditionValues);
    } else {
      label = i18n.translate(label);
    }

    return {
      ...option,
      label,
      tooltip
    };
  });

  return result;
};

/**
 * Handle leave page - default confirm from browser
 *
 * @param  {any} e Event
 *
 * @return {any} Return ''
 */
export const confirmLeavePage = (event: Object, shouldConfirm: boolean, i18n: Object) => {
  const e = event || window.event;
  e.preventDefault();

  // Message only affects in some browsers, another browsers will show default text
  const message = i18n.translate('AdminHTML.Common.ConfirmLeaveModal.content');

  if (shouldConfirm) {
    e.returnValue = message;
    return message;
  }
  return '';
};

export const encodeHtmlCode = (pattern: string) => {
  const specialCharacters = ['$'];

  const arrayString = pattern.split('');
  for (let index = 0; index < arrayString.length; index++) {
    if (specialCharacters.includes(arrayString[index])) {
      arrayString[index] = `&#${arrayString[index].charCodeAt(0)};`;
    }
  }

  return arrayString.join('');
};

export const decodeHtmlCode = (pattern: string) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = pattern;
  const decodeValue = textarea.value;

  textarea.remove();
  return decodeValue;
};

/**
 * Display price based on money format
 * @param {*} price
 * @returns
 */
export const formatPricing = (price, moneyFormat) => {
  // Convert to thousand number
  price = formatNumberInThousand(price);

  return moneyFormat
    .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/, price)
    .replace(/{{\s*amount_no_decimals\s*}}/, price)
    .replace(/{{\s*amount_with_comma_separator\s*}}/, price)
    .replace(/{{\s*amount\s*}}/, price);
};

/**
 * Reorder index of element in array
 * @param {Array} list
 * @param {number} startIndex
 * @param {number} endIndex
 * @returns {Array} result  // Reordered array
 */
export const reorder = (list: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

/**
 * Reorder index of element in two array
 * @param {Array} sourceList
 * @param {Array} destList
 * @param {number} startIndex
 * @param {number} endIndex
 * @returns {Array} result  // Reordered array
 */
export const reorderTwoList = (
  sourceList: any[],
  destList: any[],
  startIndex: number,
  endIndex: number
) => {
  const resultSource = Array.from(sourceList);
  const resultDest = Array.from(destList);
  const [removed] = resultSource.splice(startIndex, 1);
  resultDest.splice(endIndex, 0, removed);

  return [resultSource, resultDest];
};

/**
 * Check value is a numeric
 * @param {string | number} value
 * @returns {Boolean}
 */
export const isNumeric = value => {
  return /^\d+$/.test(value);
};

/**
 * Create a script with src
 * @param {string} src
 * @returns {HTMLScriptElement}
 */
export const loadScript = (src, resetCache = true, hasAsync = true, container = document) => {
  const script = container.createElement('script');
  if (resetCache) {
    script.src = `${src}?v=${Date.now()}`;
  } else script.src = src;
  script.type = 'text/javascript';
  if (hasAsync) {
    script.async = true;
  }
  container.body.append(script);
  return script;
};

/**
 * Create a script with src
 * @param {string} src
 * @returns {HTMLScriptElement}
 */
export const loadScriptWithCallback = (src, callback, container = document) => {
  const script = container.createElement('script');
  script.src = src;
  script.type = 'text/javascript';
  // Set up a callback for the script load event
  script.onload = callback;

  container.body.append(script);
  return script;
};

/**
 * Load file css with href
 * @param {string} href
 * @returns {HTMLLinkElement}
 */
export const loadCSS = (href, container = document) => {
  const link = container.createElement('link');
  link.href = `${href}?v=${Date.now()}`;
  link.type = 'text/css';
  link.rel = 'stylesheet';

  container.body.append(link);

  return link;
};

/**
 * Remove a script if document contains it
 * @param {HTMLScriptElement} script
 * @returns
 */
export const removeScript = (script, container = document) => {
  if (container.body.contains(script)) {
    container.body.removeChild(script);
  }
};

/**
 * Get an object with "published": true or the object with the latest "updatedAt" from an array of themes.
 * @param {Object[]} themes - An array of objects containing theme information.
 * @returns {Object} - An object that satisfies the condition, or an empty array if themes is empty.
 */
export const getPublishedOrLatestTheme = (themes: Record<string, any>[]) => {
  if (themes.length === 0) return [];
  const publishedTheme = themes.filter(obj => obj.published === true)[0];
  if (publishedTheme) {
    return publishedTheme;
  } else {
    const latestTheme = themes.reduce((a, b) => (a.updatedAt > b.updatedAt ? a : b));
    return latestTheme;
  }
};

/**
 * Convert image file into base64 format
 * @param {File} file
 * @returns {string} base64 string
 */
export const convertImageToBase64 = file => {
  if (file) {
    const reader = new FileReader();

    return new Promise(resolve => {
      reader.onload = ev => {
        resolve(ev.target.result);
      };
      reader.readAsDataURL(file);
    });
  }
};

// Set local storage value with expiration
export function setWithExpiry(key, value, ttl) {
  const now = new Date();

  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value: value,
    expiry: now.getTime() + ttl
  };

  try {
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {}
}

// Get local storage value with expiration
export function getWithExpiry(key) {
  try {
    const itemStr = localStorage.getItem(key);
    // if the item doesn't exist, return null
    if (!itemStr) {
      return null;
    }
    const item = JSON.parse(itemStr);
    const now = new Date();
    // compare the expiry time of the item with the current time
    if (now.getTime() > item.expiry) {
      // If the item is expired, delete the item from storage
      // and return null
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  } catch (error) {}

  return null;
}

export function usePrevious(value) {
  const ref = React.useRef();
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

/**
 * Get the full url for product detail
 *
 * @param  {string}  handle
 *
 * @result {string} full url of route
 */
export function getFullUrlProductDetail(handle: string) {
  const shop = getShop();
  return `https://${shop}/products/${handle}`;
}

/**
 * Get the full url for order in admin page
 *
 * @param  {string}  handle
 *
 * @result {string} full url of route
 */
export function getFullUrlOrderAdmin(orderId: string) {
  const shop = getShop();
  return `https://${shop}/admin/orders/${orderId}`;
}

/**
 * Get the full url for product detail in admin
 *
 * @param  {string}  handle
 *
 * @result {string} full url of route
 */
export function getFullUrlProductDetailAdmin(productId: string) {
  if (!productId) return `https://admin.shopify.com/store/${getTenantName()}/products`;
  return `https://admin.shopify.com/store/${getTenantName()}/products/${productId}`;
}

/**
 * Round a number with 2 decimals
 * @param {*} num
 * @returns number
 */

export const roundToTwoDecimals = num => {
  if (num == null || Number.isInteger(num)) {
    return num;
  }
  return num.toFixed(2);
};

/**
 * Get asset url from ADMIN_APP_ASSET_PATHS
 * @param {*} path
 * @returns
 */
export const getAssetURL = name => {
  const ASSETS_CDN_URL = process.env.REACT_APP_IMAGE_URL || 'http://localhost';
  if (name) {
    return `${ASSETS_CDN_URL}/${name}`;
  }
  return `${ASSETS_CDN_URL}/no_image.png`;
};

// Function to replace placeholders dynamically
export const replacePlaceholders = (text, values) => {
  return text.replace(/\{\{(.*?)\}\}/g, (_, key) => values[key] || 'unknown');
};

// Mask email
// Result like *******@example.com
export const maskEmail = email => {
  const [name, domain] = email.split('@');
  return `${'*'.repeat(name.length)}@${domain}`;
};

// Mask phone
// Result like *** *** *198
export const maskPhone = phone => {
  return phone.replace(/\d/g, (match, index, array) => {
    return index < array.length - 3 ? '*' : match;
  });
};

export const isInValidDomain = (value: any, i18n) => {
  const domainRegex = /^(?!-)(?!.*--)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}(?<!-)$/;
  return !domainRegex.test(value)
    ? i18n.translate('AdminHTML.Helpers.FormValidationHelper.isValidDomain')
    : undefined;
};

// mark plan levels by number
export const getStorePlanLevel = (planName: string) => {
  if (planName.includes(ENTERPRISE)) return 4;
  if (planName.includes(PROFESSIONAL) || planName.includes(ACCELERATE)) return 3;
  if (planName.includes(ESSENTIAL) || planName.includes(CONVERT)) return 2;
  if (planName.includes(BASIC) || planName.includes(LAUNCH)) return 1;
  if (planName.includes(FREEMIUM)) return 0;
  return 1; // Default is basic plan
};

// from sub v3 to higher version
export const isNewPackageName = (name: string) => {
  return [BASIC, ESSENTIAL, PROFESSIONAL, FREEMIUM, LAUNCH, CONVERT, ACCELERATE].includes(name);
};

// get correct package name to display
export const getCurrentPackageText = tenantApp => {
  if (tenantApp?.selectedPlan?.includes('basic')) {
    return 'AdminHTML.SubscriptionV4.Packages.basic.titlePlan';
  }
  if (tenantApp?.selectedPlan?.includes('essential')) {
    return 'AdminHTML.SubscriptionV4.Packages.essential.titlePlan';
  }
  if (tenantApp?.selectedPlan?.includes('professional')) {
    return 'AdminHTML.SubscriptionV4.Packages.professional.titlePlan';
  }
  // pricing v5
  if (tenantApp?.selectedPlan?.includes('launch')) {
    return 'AdminHTML.SubscriptionV5.ExplorePackages.launch';
  }
  if (tenantApp?.selectedPlan?.includes('convert')) {
    return 'AdminHTML.SubscriptionV5.ExplorePackages.convert';
  }
  if (tenantApp?.selectedPlan?.includes('accelerate')) {
    return 'AdminHTML.SubscriptionV5.ExplorePackages.accelerate';
  }
  if (tenantApp?.selectedPlan === '') {
    return 'AdminHTML.ShopifyIntegrationSettings.SmartStyling.trialPlan';
  }
  return 'AdminHTML.SubscriptionV4.Packages.basic.titlePlan';
};

export const checkGenericEmail = (email: string) => {
  //check if it is correct email format first
  if (
    !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i.test(
      email
    )
  )
    return false;
  const genericEmailRegex =
    /^(info|support|help|contact|hello|sales|admin|office|it|dev|marketing|media|press|pr|newsletter|events|hr|accounting)@/i;

  return genericEmailRegex.test(email);
};
